// Space-aware text rendering with explicit coordinate transforms

import { CoordinateSpace, Coordinates, ContentRect } from './CoordinateSpaces';
import { SpaceTransforms } from './SpaceTransforms';
import { BackendLayoutPlan } from './BackendPlanValidator';
import { FontParityIntegration, FontLoadingConfig } from './FontParityIntegration';

export interface RenderContext {
  ctx: CanvasRenderingContext2D;
  contentRect: ContentRect;
  pageIndex?: number;
  pageHeight?: number;
  pageGap?: number;
  fontParityIntegration?: FontParityIntegration;
}

export class SpaceAwareRenderer {
  /**
   * Render text using explicit coordinate transforms
   * No blind margin addition - space-aware transforms only
   */
  static renderWithExplicitTransforms(
    plan: BackendLayoutPlan,
    renderContext: RenderContext,
    options?: {
      overrideText?: string;
    }
  ): void {
    const { ctx, contentRect } = renderContext;


    // ✅ PHASE 0 FIX: Use 'top' baseline to match caret positioning system
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#000000';

    // Handle local text override while preserving HarfBuzz layout structure
    if (options?.overrideText) {
      this.renderWithTextOverride(plan, renderContext, options.overrideText);
    } else {
      // Standard backend rendering
      // Render each line using explicit coordinate transforms
      for (const line of plan.pages[0].frames[0].lines) {
        this.renderLineWithTransforms(line, plan, renderContext);
      }
    }
  }

  /**
   * Render a line using explicit baseline and coordinate transforms
   */
  private static renderLineWithTransforms(
    line: BackendLayoutPlan['pages'][0]['frames'][0]['lines'][0],
    plan: BackendLayoutPlan,
    renderContext: RenderContext
  ): void {
    const { ctx, contentRect } = renderContext;

    // Use explicit baseline from backend (no fontSize*0.8 heuristics)
    const baselineY = line.baselineY_pagePx;


    // Render each run in the line
    for (const run of line.runs) {
      this.renderRunWithTransforms(run, baselineY, plan, renderContext);
    }
  }

  /**
   * Render a run using space-aware coordinate transforms
   */
  private static renderRunWithTransforms(
    run: BackendLayoutPlan['pages'][0]['frames'][0]['lines'][0]['runs'][0],
    baselineY: number,
    plan: BackendLayoutPlan,
    renderContext: RenderContext
  ): void {
    const { ctx, contentRect } = renderContext;

    if (!run.text || typeof run.text !== 'string') {
      return; // Skip invalid runs
    }

    // Set font properties with parity verification
    const fontSize = run.fontSizePx || plan.fontSizePx;
    const fontFamily = run.fontFamily || plan.fontFamily;
    
    // Use font parity integration if available
    if (renderContext.fontParityIntegration) {
      renderContext.fontParityIntegration.configureCanvasWithVerifiedFont(ctx, plan, fontFamily);
    } else {
      // ✅ PHASE 0 FIX: Use backend font size directly (no canvas transform scaling)
      // Backend coordinates are already in correct coordinate space
      ctx.font = `${fontSize}px "${fontFamily}"`;
    }

    // 🎯 MEASUREMENT CONSISTENCY TEST: Verify Canvas CTM handles DPI correctly
    if (run.text && run.text.length > 0) {
      // Measure with current CTM (should already include DPI scaling)
      const frontendWidthWithCTM = ctx.measureText(run.text).width;
      
      // Measure with identity CTM for comparison
      const tempCtx = ctx.canvas.getContext('2d')!;
      tempCtx.save();
      tempCtx.setTransform(1, 0, 0, 1, 0, 0); // Identity transform
      tempCtx.font = `${fontSize}px "${fontFamily}"`;
      const frontendWidthIdentity = tempCtx.measureText(run.text).width;
      tempCtx.restore();
      
      // Calculate ratios for diagnostic (no manual DPI scaling needed)
      const ctmScale = ctx.getTransform().a;
      const widthRatio = frontendWidthWithCTM / frontendWidthIdentity;
      
    }


    // 🔧 FIX: Use charBoxes for precise character positioning instead of run.x
    let runStartX: number;
    let runEndX: number;
    let runY: number;
    
    if (run.charBoxes && run.charBoxes.length > 0) {
      // Use first character's position from charBoxes
      const firstCharBox = run.charBoxes[0];
      runStartX = firstCharBox[0]; // X position from charBox
      runY = firstCharBox[1]; // Y position from charBox
      
      // Use last character's end position from charBoxes
      const lastCharBox = run.charBoxes[run.charBoxes.length - 1];
      runEndX = lastCharBox[0] + lastCharBox[2]; // X + width
    } else {
      // Fallback to run.x if no charBoxes
      const contentCoords: Coordinates = {
        x: run.x,
        y: 0,
        space: CoordinateSpace.CONTENT
      };
      const pageCoords = SpaceTransforms.transform(
        contentCoords,
        CoordinateSpace.PAGE,
        { contentRect }
      );
      runStartX = pageCoords.x;
      runY = baselineY;
      runEndX = runStartX + ctx.measureText(run.text).width;
    }
    
    // Use backend content rectangle for right edge limit
    const rightEdgeLimit = contentRect.x + contentRect.width;

    // 🎯 HARFBUZZ GLYPH-LEVEL RENDERING: Use precise HarfBuzz advances for crisp text
    if ((run as any).positions && (run as any).positions.length > 0) {
      // Use HarfBuzz glyph positioning data for character-level rendering
      let currentX = runStartX; // Use charBox X position
      const positions = (run as any).positions;
      
      for (let i = 0; i < (run.text?.length || 0) && i < positions.length; i++) {
        const char = run.text?.[i] || '';
        const position = positions[i];
        
        // Use charBox Y position instead of baselineY
        const glyphX = currentX + (position.xOffset || 0);
        const glyphY = runY + (position.yOffset || 0);
        
        // Render individual character with original HarfBuzz positioning
        ctx.fillText(char, glyphX, glyphY);
        
        // Advance using original HarfBuzz advance for proper content area usage
        currentX += (position.xAdvance || 0);
      }
      
      
      // Debug: Show transformed HarfBuzz vs Canvas width comparison
      const harfbuzzTotalAdvance = positions.reduce((sum: number, pos: any) => sum + (pos.xAdvance || 0), 0);
      // Do NOT scale advances for content layout - use original HarfBuzz advances
      const canvasWidth = ctx.measureText(run.text).width;
      
      return; // ✅ PHASE 0 FIX: Prevent multiple rendering paths from executing
      
    } else if ((run as any).xAtGrapheme && (run as any).xAtGrapheme.length > 0) {
      // ✅ PHASE 0 FIX: Use precise xAtGrapheme positioning instead of glyphAdvances
      const xAtGrapheme = (run as any).xAtGrapheme;
      const glyphAdvances = (run as any).glyphAdvances;
      
      for (let i = 0; i < (run.text?.length || 0) && i < xAtGrapheme.length - 1; i++) {
        const char = run.text?.[i] || '';
        // Use precise xAtGrapheme positioning for pixel-perfect spacing
        const charX = runStartX + xAtGrapheme[i];
        ctx.fillText(char, charX, runY);
      }
      
      
      return; // ✅ PHASE 0 FIX: Prevent multiple rendering paths from executing
      
    } else if ((run as any).glyphAdvances && (run as any).glyphAdvances.length > 0) {
      // Fallback: Use glyphAdvances array if xAtGrapheme not available
      let currentX = runStartX;
      const glyphAdvances = (run as any).glyphAdvances;
      
      for (let i = 0; i < (run.text?.length || 0) && i < glyphAdvances.length; i++) {
        const char = run.text?.[i] || '';
        ctx.fillText(char, currentX, runY);
        // Use original HarfBuzz glyph advance for proper content area usage
        currentX += glyphAdvances[i];
      }
      
      
      return; // ✅ PHASE 0 FIX: Prevent multiple rendering paths from executing
      
    } else {
      // Final fallback: Use Canvas fillText (old behavior)
      ctx.fillText(run.text, runStartX, runY);
    }


  }

  /**
   * Render with local text override while preserving HarfBuzz layout structure
   */
  private static renderWithTextOverride(
    plan: BackendLayoutPlan,
    renderContext: RenderContext,
    overrideText: string
  ): void {
    const { ctx, contentRect } = renderContext;
    

    // Enhanced text override: Handle text overflow beyond backend layout
    const backendTextLength = plan.pages[0].frames[0].lines.reduce((total: number, line: any) => 
      total + line.runs.reduce((lineTotal: number, run: any) => lineTotal + (run.text?.length || 0), 0), 0
    );
    
    
    if (overrideText.length <= backendTextLength) {
      // Simple case: Local text fits within backend layout
      this.renderWithinBackendLayout(plan, renderContext, overrideText);
    } else {
      // Complex case: Local text overflows backend layout
      this.renderWithOverflow(plan, renderContext, overrideText, backendTextLength);
    }
    
  }

  /**
   * Render local text within existing backend layout structure
   */
  private static renderWithinBackendLayout(
    plan: BackendLayoutPlan,
    renderContext: RenderContext,
    overrideText: string
  ): void {
    let localTextIndex = 0;
    
    for (const line of plan.pages[0].frames[0].lines) {
      for (const run of line.runs) {
        const runLength = run.text?.length || 0;
        const localTextSegment = overrideText.substring(localTextIndex, localTextIndex + runLength);
        
        if (localTextSegment) {
          const modifiedRun = { ...run, text: localTextSegment };
          this.renderRunWithTransforms(modifiedRun, line.baselineY_pagePx, plan, renderContext);
        }
        
        localTextIndex += runLength;
        if (localTextIndex >= overrideText.length) return;
      }
    }
  }

  /**
   * Render local text with overflow handling beyond backend layout
   */
  private static renderWithOverflow(
    plan: BackendLayoutPlan,
    renderContext: RenderContext,
    overrideText: string,
    backendTextLength: number
  ): void {
    const { ctx, contentRect } = renderContext;
    
    
    // First, render text that fits within backend layout
    const textWithinLayout = overrideText.substring(0, backendTextLength);
    this.renderWithinBackendLayout(plan, renderContext, textWithinLayout);
    
    // Then, render overflow text with simple layout
    const overflowText = overrideText.substring(backendTextLength);
    if (overflowText.length > 0) {
      
      // Get the last line and run from backend layout
      const lastLine = plan.pages[0].frames[0].lines[plan.pages[0].frames[0].lines.length - 1];
      const lastRun = lastLine.runs[lastLine.runs.length - 1];
      const fontSize = plan.fontSizePx || 16;
      
      // Calculate X position after the last character in the backend layout
      let startX = contentRect.x;
      if (lastRun.charBoxes && lastRun.charBoxes.length > 0) {
        const lastCharBox = lastRun.charBoxes[lastRun.charBoxes.length - 1];
        startX = lastCharBox[0] + lastCharBox[2]; // X + width of last character
      } else {
        // Fallback: use run.x + text width
        startX = lastRun.x + ctx.measureText(lastRun.text).width;
      }
      
      // 🔧 FIX: Use the same Y coordinate as the backend text
      let currentY = lastLine.baselineY_pagePx; // Default to baseline
      
      // If we have charBoxes, use the actual Y coordinate from the last character
      if (lastRun.charBoxes && lastRun.charBoxes.length > 0) {
        const lastCharBox = lastRun.charBoxes[lastRun.charBoxes.length - 1];
        currentY = lastCharBox[1]; // Use the Y coordinate from the last character
      }
      
      const maxWidth = contentRect.x + contentRect.width - startX; // Remaining width on this line
      
      
      ctx.font = `${fontSize}px "${plan.fontFamily}"`;
      ctx.fillStyle = '#000000';
      
      // Simply render the overflow text starting after the last character on the same line
      ctx.fillText(overflowText, startX, currentY);
      
    }
  }

  /**
   * Render margin guides using backend's authoritative content rectangle
   */
  static renderMarginGuides(
    ctx: CanvasRenderingContext2D,
    contentRect: ContentRect,
    pageWidth: number,
    pageHeight: number
  ): void {
    ctx.save();
    ctx.strokeStyle = '#e5e7eb'; // Light gray
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]); // Dashed lines

    // Top margin line
    ctx.beginPath();
    ctx.moveTo(0, contentRect.y);
    ctx.lineTo(pageWidth, contentRect.y);
    ctx.stroke();

    // Bottom margin line
    const bottomY = contentRect.y + contentRect.height;
    ctx.beginPath();
    ctx.moveTo(0, bottomY);
    ctx.lineTo(pageWidth, bottomY);
    ctx.stroke();

    // Left margin line
    ctx.beginPath();
    ctx.moveTo(contentRect.x, 0);
    ctx.lineTo(contentRect.x, pageHeight);
    ctx.stroke();

    // Right margin line
    const rightX = contentRect.x + contentRect.width;
    ctx.beginPath();
    ctx.moveTo(rightX, 0);
    ctx.lineTo(rightX, pageHeight);
    ctx.stroke();

    ctx.restore();

  }

  /**
   * Render paper background using explicit dimensions
   */
  static renderPaperBackground(
    ctx: CanvasRenderingContext2D,
    pageWidth: number,
    pageHeight: number
  ): void {
    // Draw paper background (white)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, pageWidth, pageHeight);

    // Draw paper border
    ctx.strokeStyle = '#d1d5db'; // Gray border
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, pageWidth, pageHeight);
  }
}